/*
 * Card-date helpers for in-app preview surfaces.
 *
 * LOAD-BEARING CROSS-BOUNDARY MIRROR per CLAUDE.md rule #4 sub-rule.
 * `formatPrayedOnForCard` is an output-equivalent mirror of the
 * server's `formatPrayedOnLocal` in api/_lib/order-snapshot.ts
 * (currently line ~212). The two share LOCALE_MAP shape + Intl
 * options + UTC-fallback semantics so identical inputs produce
 * identical output strings. If you change one, change both in the
 * same commit.
 *
 * Locale tags map en->en-US, es->es-MX, pt->pt-BR (mirrors LOCALE_MAP
 * in order-snapshot.ts).
 *
 * Timezone source caveat: the server formatter uses the timezone
 * captured into PrayerSnapshot at order-creation time; the client
 * preview formatter uses the device's current Intl resolved
 * timezone (Prayer record does not carry a timezone field today).
 * These match in the common case (same device for capture +
 * preview + checkout). Cross-device sharing where the device TZ
 * has changed since capture is a best-effort approximation;
 * banked: store `timezone` on the Prayer at capture time for an
 * exact match.
 */

const SUPPORTED_LANGUAGES = ['en', 'es', 'pt'] as const;
export type SupportedCardLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LOCALE_MAP: Record<SupportedCardLanguage, string> = {
  en: 'en-US',
  es: 'es-MX',
  pt: 'pt-BR',
};

/**
 * Normalize any incoming language tag (from i18n.language, browser
 * preference, etc.) to one of the supported card languages.
 * Unsupported bases fall back to 'en'.
 *
 * WHY this exists: src/i18n/index.ts (currently line ~26) configures
 * `fallbackLng: 'en'` but NOT `supportedLngs`. That means USER-FACING
 * TEXT falls back to English when i18n's catalog lookup misses, but
 * `i18n.language` itself can hold any browser-provided tag (e.g.,
 * 'fr-FR', 'de', 'zh-TW'). Without this normalizer, a naive
 * `(i18n.language ?? 'en').slice(0, 2) as 'en' | 'es' | 'pt'` cast at
 * the call site would lie to TypeScript and let an unsupported base
 * reach formatPrayedOnForCard, breaking the locale lookup.
 */
export function normalizeCardLanguage(
  raw: string | undefined | null,
): SupportedCardLanguage {
  const base = (raw ?? 'en').slice(0, 2).toLowerCase();
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedCardLanguage)
    : 'en';
}

export function formatPrayedOnForCard(
  iso: string,
  tz: string,
  language: SupportedCardLanguage,
): string {
  const locale = LOCALE_MAP[language];
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  };
  try {
    return new Intl.DateTimeFormat(locale, opts).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' })
      .format(new Date(iso));
  }
}
